import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ProductData } from "../../api/productData";

interface Props {
  product: ProductData;
}

const ProductDetailsContent: React.FC<Props> = ({ product }) => {
  const [activeAccordion, setActiveAccordion] = useState<number>(1);

  const toggleAccordion = (index: number) => {
    setActiveAccordion(activeAccordion === index ? -1 : index);
  };

  return (
    <>
      <section className="service-details pos-rel">
        <div className="container">
          {/* ========= HERO BANNER IMAGE ========= */}
          <div className="single-item-image service-det-img mb-75">
            <img src={product.heroImage} alt={product.name} />
          </div>

          {/* ========= TITLE & TEXT ========= */}
          <h2 className="details-content-title mb-15">{product.tagline}</h2>

          <p>{product.description}</p>

          <p className="mt-30">{product.descriptionExtra}</p>

          {/* ========= AEO CONVERSATIONAL FRAGMENT ========= */}
          {product.aeoFragment && (
            <article className="aeo-fragment mt-30" style={{ padding: "20px", background: "rgba(186, 107, 231, 0.05)", borderLeft: "4px solid #ba6be7", borderRadius: "0 8px 8px 0" }}>
              <p style={{ margin: 0, fontWeight: 500, fontStyle: "italic", color: "#fff" }}>
                {product.aeoFragment}
              </p>
            </article>
          )}

          {/* ========= PROCESS (FAQ ACCORDION) ========= */}
          <div className="service-process-wrap pt-120 pb-125">
            <div className="row mt-none-50 align-items-center">
              {/* LEFT SIDE ACCORDION */}
              <div className="col-lg-6 mt-50">
                <h2 className="details-content-title mb-40">
                  {product.processTitle}
                </h2>

                <div className="service_process_faq">
                  <div className="accordion">
                    {product.processSteps.map((item) => (
                      <div className="accordion-item" key={item.id}>
                        <div
                          className={`accordion-button ${
                            activeAccordion === item.id ? "" : "collapsed"
                          }`}
                          role="button"
                          onClick={() => toggleAccordion(item.id)}
                        >
                          {item.title}
                        </div>

                        <div
                          className={`accordion-collapse collapse ${
                            activeAccordion === item.id ? "show" : ""
                          }`}
                        >
                          <div className="accordion-body">
                            <p className="m-0">{item.content}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* RIGHT SIDE CLICKABLE LIST */}
              <div className="col-lg-6 mt-50">
                <ul className="content_layer_group list-unstyled">
                  {product.processSteps.map((item) => (
                    <li
                      key={item.id}
                      onClick={() => toggleAccordion(item.id)}
                      className={activeAccordion === item.id ? "active" : ""}
                    >
                      <span>{item.title.replace(/^\d+\.\s/, "")}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* ========= OUTCOME ========= */}
          <div className="services-outcome-wrap">
            <h2 className="details-content-title mb-15">
              {product.outcomeTitle}
            </h2>
            <p>{product.outcomeDescription}</p>

            <ul className="service-outcome-list list-unstyled mt-35">
              {product.outcomeItems.map((item, i) => (
                <li key={i}>
                  <span>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        opacity="0.2"
                        d="M24 12C24 13.024 22.742 13.868 22.49 14.812C22.23 15.788 22.888 17.148 22.394 18.002C21.892 18.87 20.382 18.974 19.678 19.678C18.974 20.382 18.87 21.892 18.002 22.394C17.148 22.888 15.788 22.23 14.812 22.49C13.868 22.742 13.024 24 12 24C10.976 24 10.132 22.742 9.188 22.49C8.212 22.23 6.852 22.888 5.998 22.394C5.13 21.892 5.026 20.382 4.322 19.678C3.618 18.974 2.108 18.87 1.606 18.002C1.112 17.148 1.77 15.788 1.51 14.812C1.258 13.868 0 13.024 0 12C0 10.976 1.258 10.132 1.51 9.188C1.77 8.212 1.112 6.852 1.606 5.998C2.108 5.13 3.618 5.026 4.322 4.322C5.026 3.618 5.13 2.108 5.998 1.606C6.852 1.112 8.212 1.77 9.188 1.51C10.132 1.258 10.976 0 12 0C13.024 0 13.868 1.258 14.812 1.51C15.788 1.77 17.148 1.112 18.002 1.606C18.87 2.108 18.974 3.618 19.678 4.322C20.382 5.026 21.892 5.13 22.394 5.998C22.888 6.852 22.23 8.212 22.49 9.188C22.742 10.132 24 10.976 24 12Z"
                        fill="#ba6be7"
                      />
                      <path
                        d="M15.5559 9.14076L11.3992 13.178L9.24437 11.0869C8.77664 10.6326 8.01773 10.6326 7.55001 11.0869C7.08229 11.5412 7.08229 12.2783 7.55001 12.7326L10.5729 15.6686C11.0279 16.1105 11.7668 16.1105 12.2218 15.6686L17.2484 10.7864C17.7162 10.3321 17.7162 9.59504 17.2484 9.14076C16.7807 8.68648 16.0236 8.68648 15.5559 9.14076Z"
                        fill="#ba6be7"
                      />
                    </svg>
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ========= BOTTOM CTA SECTION ========= */}
      <section className="product-cta-section">
        <div className="container">
          <div className="product-cta-card">
            <div className="product-cta-inner">
              <h2 className="product-cta-heading">
                {product.ctaHeading}
              </h2>
              <p className="product-cta-text">
                {product.ctaText}
              </p>
              <a href="https://dashboard.torqai.ca/" className="thm-btn agency-btn" target="_blank" rel="noopener noreferrer">
                <span className="text">{product.ctaButton}</span>
                <span className="arrow">
                  <span className="arrow-icon">
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="5.06592" y="19.9785" width="20.5712" height="2.61221" transform="rotate(-40.2798 5.06592 19.9785)" fill="white"/>
                      <rect x="7.97095" y="7.24463" width="2.61221" height="2.61221" transform="rotate(-40.2798 7.97095 7.24463)" fill="white"/>
                      <rect x="11.6523" y="7.54834" width="2.61221" height="2.61221" transform="rotate(-40.2798 11.6523 7.54834)" fill="white"/>
                      <rect x="15.334" y="7.85205" width="2.61221" height="2.61221" transform="rotate(-40.2798 15.334 7.85205)" fill="white"/>
                      <rect x="18.7119" y="11.8374" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.7119 11.8374)" fill="white"/>
                      <rect x="18.4084" y="15.52" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.4084 15.52)" fill="white"/>
                      <rect x="18.104" y="19.2012" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.104 19.2012)" fill="white"/>
                    </svg>
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="5.06592" y="19.9785" width="20.5712" height="2.61221" transform="rotate(-40.2798 5.06592 19.9785)" fill="white"/>
                      <rect x="7.97095" y="7.24463" width="2.61221" height="2.61221" transform="rotate(-40.2798 7.97095 7.24463)" fill="white"/>
                      <rect x="11.6523" y="7.54834" width="2.61221" height="2.61221" transform="rotate(-40.2798 11.6523 7.54834)" fill="white"/>
                      <rect x="15.334" y="7.85205" width="2.61221" height="2.61221" transform="rotate(-40.2798 15.334 7.85205)" fill="white"/>
                      <rect x="18.7119" y="11.8374" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.7119 11.8374)" fill="white"/>
                      <rect x="18.4084" y="15.52" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.4084 15.52)" fill="white"/>
                      <rect x="18.104" y="19.2012" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.104 19.2012)" fill="white"/>
                    </svg>
                  </span>
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProductDetailsContent;
